import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Button, Form, Row, Col, Spinner, Alert, Badge, Card } from "react-bootstrap";
import { FaPlus, FaEdit, FaTrash, FaStar } from "react-icons/fa";
import Swal from "sweetalert2";

import {
  getAllTestimonials,
  createTestimonial,
  updateTestimonial,
  deleteTestimonial,
  getAdminLookups,
} from "../../services/adminApi";

import ReusableAdminTable from "./ReusableAdminTable";

import "./AdminTestimonials.css";

const AdminTestimonials = () => {
  // =========================================
  // STATES
  // =========================================

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showForm, setShowForm] = useState(false);

  const [editId, setEditId] = useState(null);

  const [error, setError] = useState("");

  const [lookupGroups, setLookupGroups] = useState([]);

  const [formData, setFormData] = useState({
    name: "",
    talent_name: "",
    description: "",
    rating_star: 5,
    status: true,
  });

  // =========================================
  // LOAD LOOKUPS
  // =========================================

  const loadLookups = useCallback(async () => {
    try {
      const res = await getAdminLookups();

      const filtered = res?.data?.filter((lk) =>
        ["jobTalent"].includes(lk.lookupGroup)
      );

      setLookupGroups(filtered || []);
    } catch (err) {
      console.log(err);
    }
  }, []);

  // =========================================
  // FETCH TESTIMONIALS
  // =========================================

  const fetchTestimonials = useCallback(async () => {
    try {
      setLoading(true);

      const res = await getAllTestimonials();

      setList(res?.data?.data || []);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Failed to load testimonials"
      );
    } finally {
      setLoading(false);
    }
  }, []);

  // =========================================
  // INITIAL LOAD
  // =========================================

  useEffect(() => {
    fetchTestimonials();
    loadLookups();
  }, [fetchTestimonials, loadLookups]);

  // =========================================
  // TALENT LIST
  // =========================================

  const talentNames =
    lookupGroups.find(
      (g) => g.lookupGroup === "jobTalent"
    )?.lookupValue?.data || [];

  // =========================================
  // CHANGE
  // =========================================

  const handleChange = (e) => {
    const { name, value, type, checked } =
      e.target;

    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox" ? checked : value,
    }));
  };

  // =========================================
  // RESET FORM
  // =========================================

  const resetForm = () => {
    setEditId(null);

    setShowForm(false);

    setFormData({
      name: "",
      talent_name: "",
      description: "",
      rating_star: 5,
      status: true,
    });
  };

  // =========================================
  // SUBMIT
  // =========================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (
        !formData.name ||
        !formData.talent_name ||
        !formData.description
      ) {
        Swal.fire({
          icon: "warning",
          title: "Required",
          text: "Please fill all fields",
          confirmButtonColor: "#e10600",
          timer: 2000,
          showConfirmButton: false,
        });

        return;
      }

      if (editId) {
        await updateTestimonial(editId, formData);

        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Updated Successfully",
          confirmButtonColor: "#e10600",
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        await createTestimonial(formData);

        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Created Successfully",
          confirmButtonColor: "#e10600",
          timer: 2000,
          showConfirmButton: false,
        });
      }

      resetForm();

      fetchTestimonials();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          err?.response?.data?.message ||
          "Save failed",
        confirmButtonColor: "#c30000",
      });
    }
  };

  // =========================================
  // EDIT
  // =========================================

  const handleEdit = (item) => {
    setEditId(item.id);

    setShowForm(true);

    setFormData({
      name: item.name || "",
      talent_name: item.talent_name || "",
      description: item.description || "",
      rating_star: item.rating_star || 5,
      status: item.status,
    });

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // =========================================
  // DELETE
  // =========================================

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Delete this testimonial?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#c30000",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, Delete",
    });

    if (!result.isConfirmed) return;

    try {
      await deleteTestimonial(id);

      Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Deleted Successfully",
        confirmButtonColor: "#e10600",
        timer: 2000,
        showConfirmButton: false,
      });

      fetchTestimonials();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Delete Failed",
        text:
          err?.response?.data?.message ||
          "Delete failed",
        confirmButtonColor: "#c30000",
      });
    }
  };

  // =========================================
  // TABLE COLUMNS
  // =========================================

  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Name",
      },

      {
        accessorKey: "talent_name",
        header: "Talent",
      },

      {
        accessorKey: "rating_star",
        header: "Rating",

        cell: (info) => (
          <div className="d-flex gap-1 text-warning">
            {[1, 2, 3, 4, 5].map((star) => (
              <FaStar
                key={star}
                size={14}
                opacity={
                  star <= info.getValue() ? 1 : 0.3
                }
              />
            ))}
          </div>
        ),
      },

      {
        accessorKey: "status",
        header: "Status",

        cell: (info) => (
          <Badge
            bg={
              info.getValue()
                ? "success"
                : "secondary"
            }
          >
            {info.getValue()
              ? "ACTIVE"
              : "INACTIVE"}
          </Badge>
        ),
      },

            {
        id: "actions",
        header: () => <div style={{ textAlign: "center", width: "100%" }}>ACTIONS</div>,

        cell: (info) => (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "8px", width: "100%", margin: "0 auto" }}>
            {/* ✅ EDIT BUTTON: ROYAL BLUE */}
            <button
              type="button"
              style={{
                background: "#2563eb",
                backgroundColor: "#2563eb",
                color: "#ffffff",
                border: "none",
                borderRadius: "6px",
                width: "32px",
                height: "32px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                padding: 0,
                flexShrink: 0
              }}
              onClick={() =>
                handleEdit(info.row.original)
              }
              title="Edit Testimonial"
            >
              <FaEdit size={14} color="#ffffff" />
            </button>

            {/* ✅ DELETE BUTTON: CRIMSON RED */}
            <button
              type="button"
              style={{
                background: "#dc2626",
                backgroundColor: "#dc2626",
                color: "#ffffff",
                border: "none",
                borderRadius: "6px",
                width: "32px",
                height: "32px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                padding: 0,
                flexShrink: 0
              }}
              onClick={() =>
                handleDelete(info.row.original.id)
              }
              title="Delete Testimonial"
            >
              <FaTrash size={14} color="#ffffff" />
            </button>
          </div>
        ),
      },
    ],
    []
  );

  // =========================================
  // RENDER
  // =========================================

  return (
    <div className="testimonial-page">
      {/* ========================================= */}
      {/* HEADER */}
      {/* ========================================= */}

      {!showForm && (
        <ReusableAdminTable
          data={list}
          columns={columns}
          isLoading={loading}
          title="Testimonials"
          onAddNew={() => {
            resetForm();
            setShowForm(true);
          }}
          addNewButtonText="Create Testimonial"
          searchPlaceholder="Search testimonials..."
          exportCsvFilename="testimonials.csv"
        />
      )}

      {/* ========================================= */}
      {/* FORM */}
      {/* ========================================= */}

      {showForm && (
        <Card className="testimonial-form-card bg-dark text-white border-secondary">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h3>
                {editId
                  ? "Edit Testimonial"
                  : "Add Testimonial"}
              </h3>

              <Button
                variant="secondary"
                onClick={async () => {
                  const result =
                    await Swal.fire({
                      title:
                        "Discard Changes?",
                      text:
                        "Unsaved changes will be lost.",
                      icon: "warning",
                      showCancelButton: true,
                      confirmButtonColor:
                        "#c30000",
                      cancelButtonColor:
                        "#6b7280",
                      confirmButtonText:
                        "Yes, Cancel",
                    });

                  if (!result.isConfirmed)
                    return;

                  resetForm();
                }}
              >
                Cancel
              </Button>
            </div>

            <Form onSubmit={handleSubmit}>
              <Row>
                {/* NAME */}
                <Col md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label>
                      Name
                    </Form.Label>

                    <Form.Control
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="bg-dark text-white border-secondary"
                    />
                  </Form.Group>
                </Col>

                {/* TALENT */}
                <Col md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label>
                      Talent Name
                    </Form.Label>

                    <Form.Select
                      name="talent_name"
                      value={
                        formData.talent_name
                      }
                      onChange={handleChange}
                      className="bg-dark text-white border-secondary"
                    >
                      <option value="">
                        Select Talent
                      </option>

                      {talentNames.map(
                        (item, index) => (
                          <option
                            key={index}
                            value={
                              item.lookupLabel
                            }
                          >
                            {
                              item.lookupLabel
                            }
                          </option>
                        )
                      )}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              {/* DESCRIPTION */}
              <Form.Group className="mb-4">
                <Form.Label>
                  Description
                </Form.Label>

                <Form.Control
                  as="textarea"
                  rows={4}
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  className="bg-dark text-white border-secondary"
                />
              </Form.Group>

              <Row>
                {/* STAR RATING */}
                <Col md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label>
                      Rating
                    </Form.Label>

                    <div className="d-flex gap-2 mt-2">
                      {[1, 2, 3, 4, 5].map(
                        (star) => (
                          <FaStar
                            key={star}
                            size={28}
                            style={{
                              cursor: "pointer",
                              color:
                                star <=
                                formData.rating_star
                                  ? "#ffc107"
                                  : "#555",
                            }}
                            onClick={() =>
                              setFormData({
                                ...formData,
                                rating_star:
                                  star,
                              })
                            }
                          />
                        )
                      )}
                    </div>
                  </Form.Group>
                </Col>

                {/* STATUS */}
                <Col md={6}>
                  <Form.Group className="mt-5">
                    <Form.Check
                      type="checkbox"
                      label="Active"
                      name="status"
                      checked={
                        formData.status
                      }
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* BUTTONS */}
              <div className="d-flex gap-2">
                <Button
                  type="submit"
                  className="save-btn"
                >
                  {editId
                    ? "Update"
                    : "Save"}
                </Button>

                <Button
                  type="button"
                  variant="secondary"
                  onClick={resetForm}
                >
                  Cancel
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      )}

      {/* ERROR */}
      {error && (
        <Alert variant="danger">
          {error}
        </Alert>
      )}
    </div>
  );
};

export default AdminTestimonials;